import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Section, Text } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import moment from 'moment'

import Callout from 'modules/core/components/Callout'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { TrackingProperties, track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useAttendanceContextlessPolicies } from 'modules/attendance/hooks/useAttendanceContextlessPolicies'
import { useFetchEmployeesSettings } from 'modules/attendance/hooks/useFetchEmployeesSettings'
import { CustomTimeRangeCategoriesCollection } from 'modules/time_categories_settings/hooks/useFetchTimeSettingsCustomTimeRangeCategories'
import type { CustomTimeRangeCategoryFormType } from 'modules/time_categories_settings/lib/forms/CreateCustomTimeRangeCategoryForm'
import CreateCustomTimeRangeCategoryForm from 'modules/time_categories_settings/lib/forms/CreateCustomTimeRangeCategoryForm'

import ArchivedCategories from './ArchivedCategories'
import CategoryCard from './Card'
import EmptyCard from './Card/Empty'
import CategoryModal from './CategoryModal'
import TimeTrackingPolicySelectorModal from './TimeTrackingPolicySelectorModal'

interface Props {
  categories: CustomTimeRangeCategoriesCollection
  companyId: number
}

const WorkTimeCategories = ({ categories, companyId }: Props) => {
  const [showCreateCategoryModal, setCreateCategoryModal] = useState(false)
  const [showTimeTrackingPolicySelectorModal, setShowTimeTrackingPolicySelectorModal] =
    useState(false)
  const { accessEditSettings } = useAttendanceContextlessPolicies()
  const [calloutOpen, setCalloutOpen] = useState(true)

  const today = dateToString(moment(), 'backend')
  const { employeesSettings, loading } = useFetchEmployeesSettings({
    startOn: today,
    endOn: today,
    companyIds: [companyId],
  })

  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const form = CreateCustomTimeRangeCategoryForm({
    companyId,
    setModalOpen: setCreateCategoryModal,
  }) as FormikProps<CustomTimeRangeCategoryFormType>

  const action = {
    label: i18n.t('time_categories.actions.add'),
    onClick: () => {
      track(trackingEvents.timeSettings.newCategoryPressed)
      setCreateCategoryModal(true)
    },
  }

  const closedTipMessage = (
    <Box alignItems="stretch">
      <Text size="200">{i18n.t('time_categories.callout.closed')}</Text>
    </Box>
  )

  const trackEvent = () => {
    const properties: TrackingProperties = {
      employees_policy_count: employeesSettings?.length,
    }

    track(trackingEvents.timeTracking.goToWorkTimeCategoryValues)
    track(trackingEvents.timeTracking.employeesPolicyCount, properties)
  }

  const onClickButton = () => {
    trackEvent()

    if (employeesSettings?.length === 1) {
      const routeParams = {
        id: 'time_tracking',
        settingId: employeesSettings[0]?.id.toString() || 'default',
        tabId: 'time_ranges',
      }

      const route = navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams)

      navigate(route)
    } else {
      setShowTimeTrackingPolicySelectorModal(true)
    }
  }

  return (
    <Section
      illustration="add-tag"
      title={i18n.t('time_categories.title')}
      description={i18n.t('time_categories.description')}
      action={action}
    >
      {!categories.customTimeRangeCategories.length ? (
        <Box paddingTop="s40">
          <EmptyCard />
        </Box>
      ) : (
        <>
          <Box paddingY="s40">
            <Box width="full" paddingY="s8" alignItems="center" gap="s8" flexDirection="column">
              {categories.customTimeRangeCategories
                .filter((category) => category.isActive)
                .map((category) => (
                  <CategoryCard key={category.id} category={category} />
                ))}
            </Box>
            <ArchivedCategories companyId={companyId} />
            {accessEditSettings && (
              <Box paddingTop="s16">
                {calloutOpen ? (
                  <Callout
                    title={i18n.t('time_categories.callout.title')}
                    description={i18n.t('time_categories.callout.description')}
                    type="info"
                    onRemove={() => setCalloutOpen(false)}
                    action={
                      <Button loading={loading} onClick={onClickButton} type="secondary">
                        {i18n.t('time_categories.callout.button_text')}
                      </Button>
                    }
                  />
                ) : (
                  closedTipMessage
                )}
              </Box>
            )}
          </Box>
        </>
      )}
      {showCreateCategoryModal && (
        <CategoryModal actionType="create" setModalOpen={setCreateCategoryModal} form={form} />
      )}
      {showTimeTrackingPolicySelectorModal && (
        <TimeTrackingPolicySelectorModal
          setModalOpen={setShowTimeTrackingPolicySelectorModal}
          employeesSettings={employeesSettings}
        />
      )}
    </Section>
  )
}

export default WorkTimeCategories
